import { ChevronLeft, CalendarDays, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { FetchAnnouncementDetailAction } from "../../Store/Actions/AnnouncementActions";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function isExpired(expiryDate) {
  if (!expiryDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(expiryDate) < today;
}

const MainAnnouncementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { detail, loader, detailError } = useSelector((state) => state.announcement);

  useEffect(() => {
    if (id) dispatch(FetchAnnouncementDetailAction(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (detailError) {
      toast.error(detailError.message || "Failed to load announcement.");
    }
  }, [detailError]);

  if (loader && !detail) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="animate-spin text-themeColor" size={32} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-6 text-sm font-popinsRegular text-gray-500">
        Announcement not found.
      </div>
    );
  }

  const expired = isExpired(detail.expiryDate);

  return (
    <div className="p-6 max-w-2xl">
      {/* Back */}
      <button
        onClick={() => navigate("/dashboard/announcements")}
        className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition mb-6"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-popinsMedium">Back to Announcements</span>
      </button>

      {/* Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {detail.imageUrl && (
          <img
            src={detail.imageUrl}
            alt={detail.title}
            className="w-full h-52 object-cover"
          />
        )}
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h1 className="font-popinsSemiBold text-xl text-gray-800">{detail.title}</h1>
            <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-popinsSemiBold ${
              expired ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
            }`}>
              {expired ? "Expired" : "Active"}
            </span>
          </div>

          <p className="font-popinsRegular text-gray-600 text-sm leading-relaxed">{detail.message}</p>

          <div className="flex items-center gap-1.5 mt-5 text-xs text-gray-400 font-popinsRegular">
            <CalendarDays size={13} />
            <span>Expires: {formatDate(detail.expiryDate)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainAnnouncementDetail;
